/**
 * Leverage Point Entity
 * High-influence data points that can dramatically shift the regression line
 * Rare but dangerous - touching causes BIG growth + line shift
 * Visual: Large, pulsing gold/yellow points with warning indicator
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';

export class LeveragePoint {
    constructor(scene, x, y, regressionLine) {
        this.scene = scene;
        this.regressionLine = regressionLine;
        this.destroyed = false;

        // Position
        this.x = x;
        this.y = y;

        // Larger than regular points - high influence!
        this.radius = 30;

        // Visual - use custom image (1080px image scaled to ~60px)
        this.sprite = scene.add.image(x, y, 'leverage');
        this.sprite.setDepth(46);
        this.baseScale = 60 / 1080;  // Scale to ~60px
        this.sprite.setScale(this.baseScale);

        // Warning text
        this.warningText = scene.add.text(x, y - 45, 'HIGH LEVERAGE', {
            fontSize: '10px',
            fontFamily: 'Courier New, monospace',
            color: '#FFD700',
            fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(47);

        // Animation
        this.pulsePhase = 0;
        this.warningFlash = 0;
    }

    update(delta, cameraX) {
        if (this.destroyed) return;

        // Faster, more aggressive pulse
        this.pulsePhase += delta * 0.008;
        this.warningFlash += delta * 0.015;

        // Pulse animation on sprite (relative to base scale)
        const pulse = 1 + Math.sin(this.pulsePhase) * 0.2;
        this.sprite.setScale(this.baseScale * pulse);

        // Update positions
        this.sprite.setPosition(this.x, this.y);
        this.warningText.setPosition(this.x, this.y - 50);
        this.warningText.setAlpha(0.5 + Math.sin(this.warningFlash) * 0.5);
    }

    // Check collision with player (circle vs circle)
    checkCollision(playerX, playerY, playerRadius) {
        if (this.destroyed) return false;

        const dx = playerX - this.x;
        const dy = playerY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        return distance < (this.radius + playerRadius);
    }

    // When hit, shift the regression line toward this point
    onHit() {
        // Dramatically shift the line toward this leverage point
        // This simulates the high influence of a leverage point
        const currentIntercept = this.regressionLine.intercept;
        const shift = (this.y - currentIntercept) * 0.5; // Pull line 50% toward this point

        this.regressionLine.targetIntercept = Phaser.Math.Clamp(
            currentIntercept + shift,
            100,
            this.scene.scale.height - 100
        );

        // Also add some slope chaos
        this.regressionLine.targetSlope += (Math.random() - 0.5) * 0.3;
        this.regressionLine.targetSlope = Phaser.Math.Clamp(
            this.regressionLine.targetSlope,
            -0.5,
            0.5
        );
    }

    isOffScreen(cameraX) {
        return this.x < cameraX - 100;
    }

    destroy() {
        this.destroyed = true;
        if (this.sprite) this.sprite.destroy();
        if (this.warningText) this.warningText.destroy();
    }
}

/**
 * Leverage Point Manager
 * Spawns rare high-leverage points that shift the regression line
 */
export class LeveragePointManager {
    constructor(scene, regressionLine) {
        this.scene = scene;
        this.regressionLine = regressionLine;

        this.leveragePoints = [];
        this.spawnTimer = 0;
        this.baseSpawnInterval = 10000; // Rare - every 10 seconds
        this.minSpawnInterval = 6000;   // Can get as fast as 6 seconds

        // Camera tracking
        this.cameraX = 0;

        // Difficulty
        this.timeSurvived = 0;
    }

    update(delta, player, cameraX) {
        this.cameraX = cameraX;
        this.timeSurvived += delta;

        // Spawn timer
        this.spawnTimer += delta;
        const currentInterval = Math.max(
            this.minSpawnInterval,
            this.baseSpawnInterval - (this.timeSurvived / 1000) * 100
        );

        if (this.spawnTimer >= currentInterval) {
            this.spawnLeveragePoint();
            this.spawnTimer = 0;
        }

        // Update all leverage points
        for (const lp of this.leveragePoints) {
            lp.update(delta, cameraX);
        }

        // Remove off-screen points
        this.leveragePoints = this.leveragePoints.filter(lp => {
            if (lp.destroyed || lp.isOffScreen(cameraX)) {
                if (!lp.destroyed) lp.destroy();
                return false;
            }
            return true;
        });

        // Check collisions
        return this.checkCollisions(player);
    }

    spawnLeveragePoint() {
        // Spawn at far right edge
        const spawnX = this.cameraX + this.scene.scale.width + 100;

        // Position at extreme Y (that's what makes it high leverage!)
        // Either very high or very low
        const extreme = Math.random() < 0.5;
        let spawnY;
        if (extreme) {
            spawnY = 80 + Math.random() * 80; // Top area
        } else {
            spawnY = this.scene.scale.height - 160 + Math.random() * 80; // Bottom area
        }

        const leveragePoint = new LeveragePoint(this.scene, spawnX, spawnY, this.regressionLine);
        this.leveragePoints.push(leveragePoint);

        // Screen shake warning
        this.scene.cameras.main.shake(100, 0.003);
    }

    checkCollisions(player) {
        const playerRadius = player.getCollisionRadius();

        for (const lp of this.leveragePoints) {
            if (lp.checkCollision(player.x, player.y, playerRadius)) {
                // Trigger line shift effect
                lp.onHit();
                lp.destroy();
                return { hit: true, type: 'leverage', growth: 0.3 };
            }
        }
        return { hit: false };
    }

    clear() {
        this.leveragePoints.forEach(lp => lp.destroy());
        this.leveragePoints = [];
    }

    destroy() {
        this.clear();
    }
}
